'use client';

/**
 * SVGHMI Preview Component (API-based)
 * Показывает preview через backend rendering
 */

import {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import {
  Row,
  Col,
  Modal,
  ModalBody,
} from 'reactstrap';
import { PopoverPicker } from '../../components/Shared/PopoverPicker';
import { renderSvg } from '../../../services/converter.service';
import SvghmiEditorOverlay from './SvghmiEditorOverlay';
import useVisualEditor from './useVisualEditor';

function SvghmiPreview({
  optimizeId,
  renderedSvgs,
  renderMetadata,
  onPreviewSvgChange,
  onPreviewLimitExceeded,
  fileName, // Для preview-only mode
  files, // Для normal mode
  renderFn, // Custom render function (for AI generator)
  editorEnabled, // Enable visual editor mode
  sourceXml, // Source SVGHMI XML string for editor
  // Keep the values the user typed into the property controls when the next
  // widget has the very same property names (AI generator: after an edit).
  keepParamValuesOnSameNames = false,
  // Extra sidebar content rendered under the property controls (AI generator:
  // download, refine, restyle). With it the layout is preview ~2/3 | sidebar
  // ~1/3 and stacks below lg; without it nothing changes for the converter.
  aside = null,
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [previewSvg, setPreviewSvg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paramValues, setParamValues] = useState({});
  // Uncommitted text typed into an HmiColor hex field, keyed by param name — kept
  // separate from paramValues so an invalid in-progress edit doesn't get sent to
  // the backend; committed (or dropped, if invalid) on Enter/blur.
  const [hexDrafts, setHexDrafts] = useState({});
  const [mounted, setMounted] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const overlayRef = useRef(null);

  const editor = useVisualEditor();

  // Both init effects below are guarded on CONTENT, not on the identity of the
  // `renderedSvgs` / `renderMetadata` arrays. Callers (e.g. AiGenerator) rebuild
  // those arrays as fresh literals on every render of THEIR OWN component, so an
  // identity-only dependency re-fires on any unrelated parent re-render and stomps
  // a live, user-edited preview/param value back to the file default mid-edit
  // (e.g. right after picking a color). Refs below remember the last-applied
  // content so a parent re-render with equal content is a no-op.
  const appliedDefaultSvgRef = useRef(null);
  const appliedParamsSignatureRef = useRef(null);
  // User values carried over to a widget with the same property names; the
  // effect after fetchRef re-renders the preview with them once.
  const reapplyValuesRef = useRef(null);

  // Monotonic sequence number for fetchRenderedSvg calls. Property-control edits
  // can fire several overlapping requests (e.g. a picked color's onClose fetch
  // racing a hex-field blur fetch); network timing gives no guarantee the LAST
  // request also RESOLVES last. Each call captures its own seq and only the call
  // that still matches the latest-issued seq at resolution time is allowed to
  // apply its result or clear the loading flag — older, now-stale responses are
  // dropped instead of clobbering a fresher one.
  const renderSeqRef = useRef(0);

  // Committed color value at the moment each HmiColor popover was opened, keyed
  // by param name — lets the popover's onClose skip a redundant fetch when the
  // user opened it and closed it again without actually picking a new color.
  const colorOpenValueRef = useRef({});

  // Устанавливаем флаг монтирования на клиенте
  useEffect(() => {
    setMounted(true);
  }, []);

  // Initialize editor when edit mode is toggled on
  const toggleEditMode = useCallback(() => {
    if (!editMode && sourceXml) {
      editor.initEditor(sourceXml);
      setEditMode(true);
    } else {
      setEditMode(false);
      editor.setSelectedId(null);
    }
  }, [editMode, sourceXml, editor]);

  // Handle move in editor: apply to source XML, then update visual via optimistic render
  const handleEditorMove = useCallback((id, dx, dy) => {
    editor.moveElement(id, dx, dy);
  }, [editor]);

  // Handle delete in editor
  const handleEditorDelete = useCallback((id) => {
    if (!editor.deleteElement(id)) return;
    // Remove element from rendered DOM via overlay ref
    overlayRef.current?.removeElement(id);
  }, [editor]);

  // Download modified SVGHMI
  const downloadModifiedXml = useCallback(() => {
    const xml = editor.getModifiedXml();
    if (!xml) return;
    const blob = new Blob([xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${optimizeId ? `ai-widget-${optimizeId.slice(0, 8)}-edited` : 'widget-edited'}.svghmi`;
    a.click();
    URL.revokeObjectURL(url);
  }, [editor, optimizeId]);

  // Устанавливаем дефолтный rendered SVG из optimize ответа
  useEffect(() => {
    if (renderedSvgs && renderedSvgs.length > currentIndex && renderedSvgs[currentIndex]) {
      const svg = renderedSvgs[currentIndex];
      // Content guard (see comment above the refs): skip if this is the same
      // default svg we already applied, so a spurious parent re-render doesn't
      // overwrite a live-rendered (e.g. re-colored) preview with the file default.
      if (svg === appliedDefaultSvgRef.current) return;
      appliedDefaultSvgRef.current = svg;
      setPreviewSvg(svg);
      if (onPreviewSvgChange) {
        onPreviewSvgChange(svg);
      }
    }
  }, [renderedSvgs, currentIndex, onPreviewSvgChange]);

  // Инициализируем значения параметров из renderMetadata
  useEffect(() => {
    const hasParams = renderMetadata
      && renderMetadata.length > currentIndex
      && renderMetadata[currentIndex]?.params;

    if (!hasParams) {
      // A widget without properties must not inherit the previous widget's
      // values (the controls follow renderMetadata, the values follow this).
      if (appliedParamsSignatureRef.current !== null) {
        appliedParamsSignatureRef.current = null;
        setParamValues({});
      }
      return;
    }

    const { params } = renderMetadata[currentIndex];
    // Content guard: same reasoning as appliedDefaultSvgRef above — only reset
    // paramValues when the actual param set (name/type/value) changed, not just
    // because the parent handed us a new-but-equal array reference.
    const signature = JSON.stringify(params.map((p) => [p.name, p.type, p.value]));
    if (signature === appliedParamsSignatureRef.current) return;
    appliedParamsSignatureRef.current = signature;

    const initialValues = {};
    params.forEach((param) => {
      initialValues[param.name] = param.value;
    });
    setParamValues((prev) => {
      if (!keepParamValuesOnSameNames) return initialValues;
      const names = Object.keys(initialValues).sort().join('\n');
      const prevNames = Object.keys(prev || {}).sort().join('\n');
      if (!names || names !== prevNames) return initialValues;
      const changed = Object.keys(initialValues)
        .some((k) => String(prev[k]) !== String(initialValues[k]));
      if (!changed) return initialValues;
      reapplyValuesRef.current = prev;
      return prev;
    });
  }, [renderMetadata, currentIndex, keepParamValuesOnSameNames]);

  // Запрос на backend для рендеринга с новыми значениями
  const fetchRenderedSvg = useCallback(async (newValues) => {
    // optimizeId drives the default (converter) render path; a custom renderFn
    // (AI generator / import-mode) is a self-contained alternative that doesn't
    // need one — e.g. imported widgets have no job id to key off of.
    if (!optimizeId && !renderFn) return;

    renderSeqRef.current += 1;
    const seq = renderSeqRef.current;
    setLoading(true);
    try {
      // Формируем mockValues в формате { ParamProps: {...} }
      const mockValues = { ParamProps: newValues };
      const data = renderFn
        ? await renderFn(mockValues)
        : await renderSvg(optimizeId, mockValues);

      // A newer request was issued while this one was in flight — its response
      // (if any) will apply instead; this one is stale, drop it silently.
      if (seq !== renderSeqRef.current) return;

      // Check if preview limit exceeded
      if (data.previewLimitExceeded) {
        console.warn('Preview limit exceeded:', data.msg);
        if (onPreviewLimitExceeded) {
          onPreviewLimitExceeded();
        }
        // Keep current preview, don't update
        return;
      }

      if (data.status && data.svg) {
        setPreviewSvg(data.svg);
        if (onPreviewSvgChange) {
          onPreviewSvgChange(data.svg);
        }
      }
    } catch (error) {
      if (seq === renderSeqRef.current) {
        console.error('Error fetching rendered SVG:', error);
      }
    } finally {
      // Only the latest request may clear the spinner — an older request
      // finishing after a newer one started must not hide the newer one's
      // in-flight state.
      if (seq === renderSeqRef.current) {
        setLoading(false);
      }
    }
  }, [optimizeId, renderFn, onPreviewSvgChange, onPreviewLimitExceeded]);

  // Debounce для text/number параметров
  const fetchRef = useRef(fetchRenderedSvg);
  fetchRef.current = fetchRenderedSvg;

  // Re-render once with the user's values carried over to a new widget (see
  // keepParamValuesOnSameNames); the server's default render is shown until then.
  useEffect(() => {
    const values = reapplyValuesRef.current;
    if (!values) return;
    reapplyValuesRef.current = null;
    fetchRef.current(values);
  }, [paramValues]);

  const debouncedFetchRenderedSvg = useMemo(
    () => {
      let timeoutId;
      return (values) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          fetchRef.current(values);
        }, 500);
      };
    },
    [],
  );

  // Обновление значения параметра (без запроса к бэкенду)
  // Stable identity (functional state update, no closed-over deps) — hygiene so
  // consumers that receive this as a prop (e.g. PopoverPicker's onChange) don't
  // see it change every render.
  const updateParamValue = useCallback((paramName, value) => {
    setParamValues((prev) => ({
      ...prev,
      [paramName]: value,
    }));
  }, []);

  // Отправка запроса с текущими значениями (для sliders и color picker)
  const triggerFetch = (paramName, value) => {
    const newValues = {
      ...paramValues,
      [paramName]: value,
    };
    fetchRenderedSvg(newValues);
  };

  // Обработка изменения значения параметра
  const handleParamChange = (paramName, value, paramType) => {
    const newValues = {
      ...paramValues,
      [paramName]: value,
    };
    setParamValues(newValues);

    // Для number/text - используем debounce, для остальных - сразу
    if (paramType === 'number' || paramType === 'text') {
      debouncedFetchRenderedSvg(newValues);
    } else {
      fetchRenderedSvg(newValues);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (renderedSvgs && currentIndex < renderedSvgs.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  // Конвертация hmiColor в hex для отображения в color input
  const hmiColorToHex = (hmiColor) => {
    if (hmiColor === undefined || hmiColor === null) {
      return '#000000';
    }
    // render_metadata param defaults arrive as strings, and some come as '0x…'
    // hex literals (e.g. "0xFFCD2322"). parseInt(str, 10) stops at the 'x' and
    // silently yields 0 — Number() natively understands the '0x' prefix.
    const num = typeof hmiColor === 'number' ? hmiColor : Number(hmiColor);
    if (!Number.isFinite(num)) {
      return '#000000';
    }
    const r = Math.floor(num / 65536) % 256;
    const g = Math.floor(num / 256) % 256;
    const b = num % 256;
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  };

  // Конвертация hex в hmiColor
  const hexToHmiColor = (hex) => {
    const r = parseInt(hex.substring(1, 3), 16);
    const g = parseInt(hex.substring(3, 5), 16);
    const b = parseInt(hex.substring(5, 7), 16);

    return (0xFF * 16777216) + (r * 65536) + (g * 256) + b;
  };

  const hasPreview = renderedSvgs && renderedSvgs.length > 0;
  const currentParams = renderMetadata && renderMetadata.length > currentIndex
    ? renderMetadata[currentIndex]?.params || []
    : [];

  // Determine file name to display
  const displayFileName = fileName || (files && files[currentIndex]) || null;

  if (!hasPreview) {
    return (
      <div className="text-center text-muted p-4">
        <p>No preview available. Please optimize first.</p>
      </div>
    );
  }

  // Рендер контрола для boolean
  // In the AI generator's sidebar (`aside`) every property takes the full
  // width: two per row did not fit (owner, 2026-10-13).
  const paramCol = aside ? 'col-12' : 'col-12 col-md-6';

  const renderBooleanControl = (param, currentValue) => (
    <div key={param.name} className={paramCol}>
      <div className="form-check">
        <input
          type="checkbox"
          className="form-check-input"
          id={`param-${param.name}`}
          // render_metadata defaults arrive as strings, so a literal 'false'
          // must not be treated as truthy the way Boolean('false') would.
          checked={currentValue === true || currentValue === 'true'}
          onChange={(e) => handleParamChange(param.name, e.target.checked, param.type)}
        />
        <label className="form-check-label" htmlFor={`param-${param.name}`}>
          {param.name}
        </label>
      </div>
    </div>
  );

  // Рендер контрола для number
  const renderNumberControl = (param, currentValue) => {
    // render_metadata param defaults arrive as strings (e.g. "50"); normalize once
    // up front so Number.isFinite()/arithmetic below see an actual number instead
    // of silently falling back to 0 for every string default.
    const numValue = typeof currentValue === 'string' ? Number(currentValue) : currentValue;
    const safeValue = Number.isFinite(numValue) ? numValue : 0;

    const isOpacity = param.name.toLowerCase().includes('opacity');
    const isAngle = param.name.toLowerCase().includes('angle');
    const isWidth = param.name.toLowerCase().includes('width');

    if (isOpacity || isAngle || isWidth) {
      const min = 0;
      let max = 50;
      if (isOpacity) {
        max = 100;
      } else if (isAngle) {
        max = 360;
      }
      const step = 1;

      return (
        <div key={param.name} className={paramCol}>
          <label className="form-label">
            {param.name}
            {isOpacity && <small className="text-muted"> (%)</small>}
          </label>
          <div className="d-flex align-items-center gap-2">
            <input
              type="range"
              className="form-range flex-grow-1"
              min={min}
              max={max}
              step={step}
              value={safeValue}
              onInput={(e) => updateParamValue(
                param.name,
                parseFloat(e.target.value),
              )}
              onMouseUp={() => triggerFetch(param.name, safeValue)}
              onTouchEnd={() => triggerFetch(param.name, safeValue)}
            />
            <span className="text-muted" style={{ minWidth: '50px' }}>
              {safeValue}
              {isOpacity && '%'}
            </span>
          </div>
        </div>
      );
    }

    return (
      <div key={param.name} className={paramCol}>
        <label className="form-label">{param.name}</label>
        <input
          type="number"
          className="form-control"
          value={safeValue}
          onChange={(e) => handleParamChange(param.name, parseFloat(e.target.value), param.type)}
        />
      </div>
    );
  };

  // Clears any in-progress hex text draft for a param (commit succeeded, was
  // invalid, or the user cancelled) — display then falls back to the derived hex.
  const clearHexDraft = (paramName) => {
    setHexDrafts((prev) => {
      if (!(paramName in prev)) return prev;
      const next = { ...prev };
      delete next[paramName];
      return next;
    });
  };

  // Validate + apply a typed hex draft on Enter/blur. Accepts '#RRGGBB' or
  // 'RRGGBB'; anything else is discarded (reverts to the last committed color).
  // No-ops (no update, no fetch) when the normalized draft is the same color
  // that's already committed — an outside click can blur this field AND close
  // the swatch popover in the same gesture; without this guard that fires two
  // redundant fetches for a value that never actually changed here.
  const commitHexDraft = (param, currentValue) => {
    const raw = hexDrafts[param.name];
    if (raw === undefined) return;
    const match = /^#?([0-9a-fA-F]{6})$/.exec(raw.trim());
    if (match) {
      const hmiColor = hexToHmiColor(`#${match[1]}`);
      const committedValue = paramValues[param.name] ?? currentValue;
      if (hmiColorToHex(committedValue) !== hmiColorToHex(hmiColor)) {
        updateParamValue(param.name, hmiColor);
        triggerFetch(param.name, hmiColor);
      }
    }
    clearHexDraft(param.name);
  };

  // Рендер контрола для HmiColor
  const renderColorControl = (param, currentValue) => {
    const hexColor = hmiColorToHex(currentValue);
    const draft = hexDrafts[param.name];
    const displayValue = draft !== undefined ? draft : hexColor;

    return (
      <div key={param.name} className={paramCol}>
        <label className="form-label">{param.name}</label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            aria-label={`${param.name} hex value`}
            value={displayValue}
            onChange={(e) => setHexDrafts((prev) => ({ ...prev, [param.name]: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitHexDraft(param, currentValue);
                e.target.blur();
              } else if (e.key === 'Escape') {
                clearHexDraft(param.name);
              }
            }}
            onBlur={() => commitHexDraft(param, currentValue)}
          />
          <PopoverPicker
            color={hexColor}
            onOpen={() => { colorOpenValueRef.current[param.name] = currentValue; }}
            onChange={(hex) => updateParamValue(param.name, hexToHmiColor(hex))}
            onClose={() => {
              const latest = paramValues[param.name] ?? currentValue;
              const openValue = colorOpenValueRef.current[param.name];
              // Skip the fetch if nothing actually changed via the swatch since
              // the popover was opened (same double-fetch concern as above).
              if (openValue !== undefined && hmiColorToHex(openValue) === hmiColorToHex(latest)) {
                return;
              }
              triggerFetch(param.name, latest);
            }}
          />
        </div>
      </div>
    );
  };

  // Рендер контрола для string
  const renderTextControl = (param, currentValue) => (
    <div key={param.name} className={paramCol}>
      <label className="form-label">{param.name}</label>
      <input
        type="text"
        className="form-control"
        value={currentValue ?? ''}
        onChange={(e) => handleParamChange(param.name, e.target.value, 'text')}
      />
    </div>
  );

  // Рендер контрола в зависимости от типа параметра
  const renderControl = (param) => {
    const currentValue = paramValues[param.name];

    if (param.type === 'boolean') {
      return renderBooleanControl(param, currentValue);
    }
    if (param.type === 'number') {
      return renderNumberControl(param, currentValue);
    }
    if (param.type === 'HmiColor') {
      return renderColorControl(param, currentValue);
    }
    return renderTextControl(param, currentValue);
  };

  // Рендер содержимого превью
  const renderPreviewContent = () => {
    if (!mounted) {
      return <p className="text-muted mb-0">Loading...</p>;
    }
    if (previewSvg) {
      if (editMode) {
        return (
          <SvghmiEditorOverlay
            ref={overlayRef}
            svgHtml={previewSvg}
            selectedId={editor.selectedId}
            onSelect={editor.setSelectedId}
            onMove={handleEditorMove}
            onDelete={handleEditorDelete}
            lockedIds={editor.lockedIds}
          />
        );
      }
      return (
        <div
          className="svghmi-preview-wrapper w-100 h-100 d-flex align-items-center justify-content-center"
          dangerouslySetInnerHTML={{ __html: previewSvg }}
        />
      );
    }
    return <p className="text-muted mb-0">Loading preview...</p>;
  };

  // Column split: preview 7 | controls 5 (converter, unchanged), or with the
  // caller's `aside` (AI generator) preview 6 | sidebar 6 on lg and 7 | 5 on
  // xl, stacked below lg. The sidebar holds properties, actions and the edit
  // box: at 4/12 it was too cramped (owner, 2026-10-13).
  const hasParams = currentParams.length > 0;
  const hasSide = hasParams || !!aside;
  let previewLg = hasSide ? 7 : 12;
  let previewMd = hasSide ? 6 : 12;
  if (aside) {
    previewMd = 12;
    previewLg = 6;
  }

  const controlsCard = hasParams ? (
    // Without an aside there is no maxHeight cap here: the card stretches to
    // match the preview column (Bootstrap row flex stretch); the body scrolls
    // internally instead of being clipped.
    <div className={`card shadow-sm${aside ? '' : ' h-100'}`} style={{ display: 'flex', flexDirection: 'column' }}>
      <div className="card-header bg-light py-1 px-2 flex-shrink-0">
        <small className="text-dark fw-semibold mb-0">Property Controls</small>
      </div>
      <div className="card-body p-2 bg-white flex-grow-1" style={{ overflowY: 'auto' }}>
        <div className="row g-2">
          {currentParams.map(renderControl)}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className="container mt-2">
      <div className="card bg-light rounded shadow border-0">
        <div className="card-header pb-0 pt-2 px-3">
          <h5 className="fs-6">
            <i className="mdi mdi-eye mdi-16px" />
            {' '}
            SVGHMI Preview
            {displayFileName && (
              <>
                {' '}
                -
                {' '}
                <span className="text-muted">{displayFileName}</span>
              </>
            )}
            {loading && <span className="spinner-border spinner-border-sm ms-2" />}
          </h5>
        </div>
        <div className="card-body">
          <div className="svghmi-preview-container">
            {/* Two-column layout: Preview + Property Controls */}
            <Row className="g-2">
              {/* Preview display - left */}
              <Col
                lg={previewLg}
                xl={aside ? 7 : undefined}
                md={previewMd}
                xs={12}
              >
                <div
                  className="card shadow-sm h-100 position-relative"
                  style={{
                    overflow: 'hidden',
                  }}
                >
                  {/* Top-right buttons (only when NOT in edit mode) */}
                  {mounted && previewSvg && !editMode && (
                    <div className="position-absolute d-flex gap-1" style={{ top: '8px', right: '8px', zIndex: 10 }}>
                      {editorEnabled && sourceXml && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          style={{ padding: '1px 6px' }}
                          onClick={toggleEditMode}
                          title="Edit layout"
                          aria-label="Edit layout"
                        >
                          <i className="mdi mdi-pencil" style={{ fontSize: '1.2rem', lineHeight: 1 }} />
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        style={{ padding: '1px' }}
                        onClick={() => setFullscreen(true)}
                        title="Fullscreen"
                        aria-label="Fullscreen"
                      >
                        <i className="mdi mdi-arrow-expand" style={{ fontSize: '1.5rem', lineHeight: 1 }} />
                      </button>
                    </div>
                  )}
                  {/* Editor toolbar */}
                  {editMode && (
                    <div
                      className="d-flex align-items-center gap-2 px-2 py-1 border-bottom"
                      style={{ background: '#f8fafc', fontSize: 13 }}
                    >
                      <span style={{ color: '#6d28d9', fontWeight: 600 }}>Edit Mode</span>
                      {editor.selectedId && (
                        <>
                          <span className="text-muted">|</span>
                          <span style={{ color: '#334155' }}>
                            Selected:
                            {' '}
                            <strong>{editor.selectedId}</strong>
                            {editor.lockedIds.has(editor.selectedId) && (
                              <span style={{ color: '#ef4444', marginLeft: 4 }}>(locked)</span>
                            )}
                          </span>
                          {!editor.lockedIds.has(editor.selectedId) && (
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              style={{ padding: '0 6px', fontSize: 12 }}
                              onClick={() => handleEditorDelete(editor.selectedId)}
                            >
                              Delete
                            </button>
                          )}
                        </>
                      )}
                      {!editor.selectedId && (
                        <span className="text-muted">Click an element to select. Drag to move. Arrow keys for precision.</span>
                      )}
                      <div className="ms-auto d-flex gap-1">
                        {editor.editCount > 0 && (
                          <button
                            type="button"
                            className="btn btn-sm btn-success"
                            style={{ padding: '0 8px', fontSize: 12 }}
                            onClick={downloadModifiedXml}
                          >
                            Download edited
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          style={{ padding: '0 6px', fontSize: 12 }}
                          onClick={toggleEditMode}
                          title="Exit editor"
                        >
                          <i className="mdi mdi-close" />
                        </button>
                      </div>
                    </div>
                  )}
                  <div
                    className="p-2 d-flex align-items-center justify-content-center bg-white flex-grow-1"
                    style={{ minHeight: '300px' }}
                  >
                    {renderPreviewContent()}
                    <style>
                      {`
                      .svghmi-preview-wrapper {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                      }
                      .svghmi-preview-wrapper svg {
                        max-width: 100%;
                        max-height: 100%;
                        width: auto;
                        height: auto;
                        display: block;
                        margin: auto;
                      }
                      `}
                    </style>
                  </div>
                </div>
              </Col>

              {/* Property controls (and the caller's sidebar) - right */}
              {hasSide && (
                <Col lg={12 - previewLg} xl={aside ? 5 : undefined} md={12 - previewMd} xs={12}>
                  {aside ? (
                    <div className="svghmi-preview-side d-flex flex-column gap-2">
                      {controlsCard}
                      {aside}
                    </div>
                  ) : controlsCard}
                </Col>
              )}
            </Row>

            {/* Navigation */}
            {renderedSvgs && renderedSvgs.length > 1 && (
              <div className="d-flex align-items-center justify-content-center mt-3 gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handlePrev}
                  disabled={currentIndex === 0}
                  style={{
                    minWidth: '70px',
                    fontSize: '0.875rem',
                  }}
                >
                  <i className="mdi mdi-chevron-left me-1" />
                  Prev
                </button>
                <span
                  className="px-3 py-1 bg-light rounded fs-6 text-muted"
                  style={{
                    minWidth: '80px',
                    textAlign: 'center',
                  }}
                >
                  {currentIndex + 1}
                  <span className="text-muted mx-1">/</span>
                  {renderedSvgs.length}
                </span>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleNext}
                  disabled={currentIndex >= renderedSvgs.length - 1}
                  style={{
                    minWidth: '70px',
                    fontSize: '0.875rem',
                  }}
                >
                  Next
                  <i className="mdi mdi-chevron-right ms-1" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fullscreen Modal */}
      {mounted && (
        <Modal
          isOpen={fullscreen}
          toggle={() => setFullscreen(false)}
          size="xl"
          centered
          style={{ maxWidth: '95vw', maxHeight: '90vh' }}
        >
          <ModalBody
            className="p-0"
            style={{
              height: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Custom Header */}
            <div className="d-flex justify-content-between align-items-center p-2 border-bottom flex-shrink-0">
              <h5 className="mb-0">
                SVGHMI Preview
                {displayFileName && (
                  <>
                    {' '}
                    -
                    {' '}
                    <span className="text-muted">{displayFileName}</span>
                  </>
                )}
              </h5>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                style={{ padding: '1px' }}
                onClick={() => setFullscreen(false)}
                title="Minimize"
                aria-label="Minimize"
              >
                <i className="mdi mdi-arrow-collapse" style={{ fontSize: '1.5rem', lineHeight: 1 }} />
              </button>
            </div>
            {/* Preview Content */}
            <div className="p-3 d-flex flex-grow-1 overflow-hidden">
              <div
                className="w-100 h-100 d-flex align-items-center justify-content-center bg-light rounded overflow-hidden"
              >
                {previewSvg ? (
                  <div
                    className="svghmi-preview-wrapper-full d-flex align-items-center justify-content-center"
                    style={{ width: '100%', height: '100%', overflow: 'hidden' }}
                    dangerouslySetInnerHTML={{ __html: previewSvg }}
                  />
                ) : (
                  <p className="text-muted">Loading preview...</p>
                )}
                <style>
                  {`
                  .svghmi-preview-wrapper-full svg {
                    max-width: 100% !important;
                    max-height: 100% !important;
                    width: auto !important;
                    height: auto !important;
                    object-fit: contain;
                  }
                  `}
                </style>
              </div>
            </div>
          </ModalBody>
        </Modal>
      )}
    </div>
  );
}

export default SvghmiPreview;
